import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CacheProgress {
  status: 'idle' | 'checking' | 'downloading' | 'extracting' | 'caching' | 'complete' | 'up-to-date' | 'error';
  progress: number;
  count?: number;
  processed?: number;
  total?: number;
  error?: string;
}

export interface CacheStatus {
  cacheExists: boolean;
  thumbnailCount: number;
  version: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ThumbnailCacheService {
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  private progressSubject = new BehaviorSubject<CacheProgress>({ status: 'idle', progress: 0 });
  private statusSubject = new BehaviorSubject<CacheStatus | null>(null);
  private isInitialized = false;

  public progress$: Observable<CacheProgress> = this.progressSubject.asObservable();
  public status$: Observable<CacheStatus | null> = this.statusSubject.asObservable();

  constructor(private ngZone: NgZone) {}

  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('[ThumbnailCache] Service workers are not supported');
      return false;
    }

    try {
      // Register the service worker
      this.serviceWorkerRegistration = await navigator.serviceWorker.register('/thumbnail-cache-sw.js', {
        scope: '/'
      });

      console.log('[ThumbnailCache] Service worker registered');

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      // Set up message handler
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event.data);
      });

      // Initialize the service worker with environment config
      this.sendMessage('INIT', {
        apiUrl: environment.api.url,
        cdnUrl: environment.cdn.url
      });

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('[ThumbnailCache] Failed to register service worker:', error);
      return false;
    }
  }

  async cacheThumbnails(): Promise<void> {
    if (!this.isInitialized) {
      const initialized = await this.initialize();
      if (!initialized) {
        this.progressSubject.next({
          status: 'error',
          progress: 0,
          error: 'Service worker not available'
        });
        return;
      }
    }

    this.sendMessage('CACHE_THUMBNAILS', {});
  }

  async getCacheStatus(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    this.sendMessage('GET_CACHE_STATUS', {});
  }

  async clearCache(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    this.sendMessage('CLEAR_CACHE', {});
  }

  private sendMessage(type: string, payload: any): void {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type, payload });
    } else {
      // Service worker not yet controlling the page, wait for it
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          registration.active.postMessage({ type, payload });
        }
      });
    }
  }

  private handleServiceWorkerMessage(data: { type: string; payload: any }): void {
    // Run inside Angular zone to trigger change detection
    this.ngZone.run(() => {
      switch (data.type) {
        case 'CACHE_PROGRESS':
          this.progressSubject.next(data.payload as CacheProgress);
          break;

        case 'CACHE_STATUS':
          this.statusSubject.next(data.payload as CacheStatus);
          break;

        case 'CACHE_ERROR':
          this.progressSubject.next({
            status: 'error',
            progress: 0,
            error: data.payload.error
          });
          break;

        case 'CACHE_CLEARED':
          this.statusSubject.next({
            cacheExists: false,
            thumbnailCount: 0,
            version: null
          });
          break;
      }
    });
  }
}
